/**
 * Investor Deck - rendered as a full-screen, brand-styled slideshow.
 *
 * Navigate with: arrow keys, page-up/down, space.
 * Print to PDF: Cmd/Ctrl+P -> "Save as PDF" with margins set to 0 and
 *   background graphics enabled. The print stylesheet stacks every slide
 *   onto its own page at 16:9 aspect.
 *
 * Source content matches /app/memory/INVESTOR_DECK.md (single source of truth).
 * To update: edit the SLIDES array below; the markdown deck remains for
 * speaker-notes use.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Maximize2, Minimize2, Printer, Crown, Download,
  ShieldCheck, AlertCircle, TrendingUp, Sparkles, Lock, Zap, Banknote, Users,
  Briefcase, Layers, Globe, ArrowRight, KeyRound, Server, FileLock2, Eye,
} from 'lucide-react';

const GOLD = '#D4AF37';

const SLIDES = [
  // 1 - Title
  {
    id: 'title',
    type: 'cover',
    title: 'Assagart',
    subtitle: 'The private asset vault. One place for everything you own.',
    tagline: 'Track. Understand. Act.',
  },
  // 2 - Problem
  {
    id: 'problem',
    type: 'problem',
    eyebrow: 'The problem',
    stat: '£3.5tn',
    statSub: 'UK private wealth "managed" in spreadsheets and email.',
    title: 'Wealth is owned. It is not coordinated.',
    bullets: [
      { icon: Layers, label: '6+ asset classes per household', body: 'Property, cars, art, watches, jewellery, wine, businesses, financials. Nobody aggregates it.' },
      { icon: Users, label: 'Advisors work in silos', body: '23% of HNW assets are underinsured at any given moment (Aon UK Private Client Report 2024).' },
      { icon: AlertCircle, label: 'Value changes are not actioned', body: 'A Patek appreciates 12% - the policy is not updated. A Ferrari is rediscovered - the will is not refreshed.' },
    ],
  },
  // 3 - Insight
  {
    id: 'insight',
    type: 'insight',
    eyebrow: 'The insight',
    title: 'Tracking is a feature. The product is action.',
    body: 'Three questions every wealthy household needs answered, instantly.',
    steps: [
      { num: '01', title: 'What do I own?', sub: 'Tracking. Table stakes.' },
      { num: '02', title: 'What is at risk?', sub: 'Risk. The differentiation.' },
      { num: '03', title: 'What should I do?', sub: 'Action. The willingness to pay.' },
    ],
    footnote: 'Mint stops at #1. Personal Capital stops at #1. We start at #2.',
  },
  // 4 - Product
  {
    id: 'product',
    type: 'product',
    eyebrow: 'The product',
    title: 'One platform. Six asset classes. One Health Score.',
    kpis: [
      { label: 'Net worth tracked', value: '£34.5M', sub: 'across 6 categories' },
      { label: 'Portfolio Health', value: '74/100', sub: 'coverage · diversification · exposure' },
      { label: 'Priority alerts', value: '3', sub: 'flagged this week' },
    ],
    bullets: [
      'Dashboard: live valuation, Health Score, priority alerts',
      'Asset card: every asset with insurance, valuation, % of portfolio',
      'Risk page: coverage ratio, underinsurance £-value, expiring policies',
      'Vault: encrypted document storage, partner share with one-time-view',
    ],
  },
  // 5 - Action layer
  {
    id: 'action',
    type: 'table',
    eyebrow: 'The action layer',
    title: 'Value -> Risk -> Action. Automated.',
    columns: ['Trigger', 'Risk surfaced', 'Action offered'],
    rows: [
      ['Property +14% (Knight Frank index)', 'New underinsurance gap of £180k', 'Connect to broker -> quote in 24h'],
      ['Watch policy expires in 30 days', 'Coverage about to lapse', 'Renew via broker partner - pre-filled'],
      ['New Ferrari acquired', 'No will amendment', 'Solicitor introduction + e-sign template'],
    ],
    footnote: 'Every value change generates a risk delta. Every risk delta generates an action. Every action is fulfilled by a partner.',
  },
  // 6 - Who pays
  {
    id: 'monetisation',
    type: 'pricing',
    eyebrow: 'Who pays',
    title: 'Three customers. Three revenue streams.',
    primaryTiers: [
      { name: 'Starter',      price: 'Free',  promise: 'Get organised.',
        body: 'Up to 5 assets · basic vault · manual tracking. The on-ramp.', icon: Users },
      { name: 'Professional', price: '£29/mo', promise: 'Run your own wealth well.',
        body: 'Unlimited assets · encrypted Document Vault · AI valuations · Risk page + Health Score · Timeline · Contextual AI Insights · Wealth Dossier PDF.',
        icon: ShieldCheck, highlight: true },
      { name: 'Elite',        price: '£79/mo', promise: 'Run it with your advisors.',
        body: 'Everything in Pro PLUS Advisor Hub (connect your broker, they see your inbox) · in-app reply thread · PII Secure Share · priority valuations · family seats · concierge.',
        icon: Crown, highlight: true },
    ],
    secondaryTiers: [
      { name: 'Broker / Wealth Mgr',
        price: '£150-500 / client / yr',
        body: 'Live client portfolios · proactive risk alerts · co-branded landing page · partner inbox of tickets + PII shares.',
        icon: Briefcase },
      { name: 'Strategic partners',
        price: 'Per-introduction',
        body: 'Insurers, auctioneers, lenders. Pre-qualified introductions at the moment of need.',
        icon: Globe },
    ],
    footnote: 'We sit in the middle. We make all three sides better off.',
  },
  // 7 - Why now
  {
    id: 'why-now',
    type: 'why-now',
    eyebrow: 'Why now',
    title: 'Three macro tailwinds.',
    points: [
      { icon: Zap,      label: 'Open Banking + HMRC API maturity', body: 'Identity, income and employment verification is a 60-second flow, not a 6-week paper trail.' },
      { icon: ShieldCheck, label: 'Consumer Duty (FCA, July 2023)', body: 'UK financial firms are legally required to evidence "good customer outcomes." Brokers urgently need tools that prove proactive care. We are that tool.' },
      { icon: Sparkles, label: 'AI valuations are finally good enough', body: 'Gemini 3 / GPT-5.2 within ±4% on watches and jewellery; ±9% on art.' },
    ],
  },
  // 8 - Competition
  {
    id: 'competition',
    type: 'matrix',
    eyebrow: 'Competition & moat',
    title: 'No one is operating at our intersection.',
    cols: ['', 'Mint / Personal Capital', 'Wealthify / Nutmeg', 'Private bank tools', 'Assagart'],
    rows: [
      ['Multi-asset (cars, art, wine)', '×', '×', 'partial', '✓'],
      ['Risk & coverage layer',         '×', '×', '×',       '✓'],
      ['AI valuations across 6 cats',   '×', '×', '×',       '✓'],
      ['Action workflow + handoff',     '×', '×', '×',       '✓'],
      ['Built for £20M+ households',    '×', '×', '✓',       '✓'],
    ],
    moat: [
      { title: 'Data network effect', body: 'More clients -> better market valuations and risk benchmarks.' },
      { title: 'Advisor relationships', body: 'Once a broker has 50 clients on Assagart, switching cost is operational, not just contractual.' },
      { title: 'Document & audit trail', body: 'Every action and policy change against the asset. We become the source of truth.' },
    ],
  },
  // 9 - Security & Data
  {
    id: 'security',
    type: 'security',
    eyebrow: 'Security & data architecture',
    title: 'Built like a bank. Read like a vault.',
    body: 'Every byte of customer data sits behind layered controls — designed so neither a careless employee, a leaked database, nor a curious advisor can ever see what they should not.',
    pillars: [
      {
        icon: KeyRound,
        title: 'Field-level encryption',
        body: 'PII (passport, NI, tax, DOB, address) is encrypted with Fernet (AES-128-CBC + HMAC-SHA256) before it touches MongoDB. Even with full DB access, the values are unreadable without the key.',
      },
      {
        icon: FileLock2,
        title: 'Documents at rest',
        body: 'Vault uploads (policies, valuations, receipts) sit in Emergent Object Storage with server-side encryption and signed URLs. Tokens expire in minutes; no public bucket access.',
      },
      {
        icon: Server,
        title: 'Transport & infra',
        body: 'TLS 1.3 everywhere. MongoDB Atlas with continuous backup. Stateless API tier behind Cloudflare. Strict security headers (HSTS, X-Frame-Options DENY, Permissions-Policy locked). Auth rate-limited at the edge.',
      },
      {
        icon: Eye,
        title: 'Access control',
        body: 'JWT-based auth + role-based authorization. PII shares are time-boxed, one-time tokens. Advisors only see clients explicitly connected to them. Admins are audited, never auto-elevated.',
      },
    ],
    compliance: [
      { label: 'GDPR-aligned', sub: 'Subject access, right to deletion, lawful basis logged' },
      { label: 'UK data residency', sub: 'Atlas region locked to UK / EU' },
      { label: 'Stripe-tokenised payments', sub: 'No card data ever touches Assagart' },
      { label: 'SOC 2 roadmap', sub: 'Targeted 2026 for partner-tier brokers' },
    ],
    footnote: 'Independent penetration test scheduled before scaling beyond 1,000 households.',
  },

  // 10 - GTM
  {
    id: 'gtm',
    type: 'gtm',
    eyebrow: 'Go-to-market',
    title: 'Wedge in via brokers. Expand to households.',
    phases: [
      { num: '01', timeline: 'Now -> 6 months', title: 'Broker partnerships',  body: '3-5 mid-size HNW insurance brokers. Co-branded portal across their 100-500 HNW clients each.', metric: '~£450k ARR · 1,500 clients' },
      { num: '02', timeline: '6 -> 18 months', title: 'HNW direct',            body: 'Self-serve sign-up for £20M+ households. PR via Spear\'s, Tatler, FT Wealth.',                  metric: '5,000 paying clients' },
      { num: '03', timeline: '18 -> 36 months', title: 'Partner monetisation', body: 'Insurer intro fees (Hiscox, Chubb, Lloyd\'s). Auction-house partnerships. UAE, USA, Switzerland.', metric: 'Mostly partner revenue' },
    ],
  },
  // 11 - Traction
  {
    id: 'traction',
    type: 'traction',
    eyebrow: 'Traction',
    title: 'Where we are today.',
    line: 'Product live and fully functional.',
    pills: [
      '6 asset categories', 'AI valuations', 'Wealth Dossier PDF', 'Partner inbox',
      'In-app reply thread', 'Document vault', 'Multi-currency', 'Encrypted PII share',
      'Stripe billing', 'Branded emails',
    ],
  },
  // 12 - Vision
  {
    id: 'vision',
    type: 'vision',
    eyebrow: 'The vision',
    title: 'The default operating system for private wealth.',
    body: 'Every wealthy household has a dozen advisors and zero coordination. We become the connective tissue. In ten years, asking "how do I manage my private wealth?" should mean signing into Assagart - the same way "how do I store files?" became Google Drive.',
    crest: true,
  },
];


// ===== Slide renderer =====

function SlideShell({ slide, index, total, children }) {
  return (
    <div className="slide-shell relative w-full h-full flex flex-col px-12 py-10 lg:px-20 lg:py-16">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-8 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        <span className="text-gold">Assagart</span>
        <span>{String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
      </div>

      {/* Eyebrow */}
      {slide.eyebrow && (
        <p className="text-gold uppercase tracking-[0.3em] text-[11px] mb-3">{slide.eyebrow}</p>
      )}

      {/* Slide body */}
      <div className="flex-1 flex flex-col justify-center">{children}</div>

      {/* Footer line */}
      <div className="mt-8 text-[10px] uppercase tracking-[0.3em] text-muted-foreground/50 flex items-center justify-between">
        <span>Per Illos Qui Perdurant</span>
        <span>Confidential - Investor draft</span>
      </div>
    </div>
  );
}

function SlideCover({ slide }) {
  return (
    <div className="text-center max-w-4xl mx-auto">
      <h1 className="font-serif text-7xl lg:text-9xl text-white mb-6 tracking-tight">{slide.title}</h1>
      <p className="text-lg lg:text-xl text-muted-foreground mb-10 leading-relaxed">{slide.subtitle}</p>
      <p className="text-gold uppercase tracking-[0.4em] text-sm">{slide.tagline}</p>
    </div>
  );
}

function SlideProblem({ slide }) {
  return (
    <div className="max-w-6xl mx-auto w-full">
      <div className="text-center mb-12">
        <p className="font-serif text-7xl lg:text-8xl text-gold mb-3">{slide.stat}</p>
        <p className="text-base text-muted-foreground max-w-2xl mx-auto">{slide.statSub}</p>
      </div>
      <h2 className="font-serif text-3xl lg:text-4xl text-white text-center mb-12">{slide.title}</h2>
      <div className="grid md:grid-cols-3 gap-6">
        {slide.bullets.map((b, i) => {
          const Icon = b.icon;
          return (
            <div key={i} className="luxury-card p-6">
              <div className="w-10 h-10 mb-4 border border-gold/30 flex items-center justify-center">
                <Icon size={18} className="text-gold" />
              </div>
              <p className="text-white text-sm font-medium mb-2">{b.label}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{b.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SlideInsight({ slide }) {
  return (
    <div className="max-w-5xl mx-auto w-full text-center">
      <h2 className="font-serif text-4xl lg:text-5xl text-white mb-4">{slide.title}</h2>
      <p className="text-muted-foreground mb-12 max-w-2xl mx-auto">{slide.body}</p>
      <div className="grid md:grid-cols-3 gap-6 mb-10">
        {slide.steps.map((s, i) => (
          <div key={i} className="luxury-card p-8">
            <p className="text-gold font-serif text-5xl mb-3">{s.num}</p>
            <h3 className="font-serif text-xl text-white mb-2">{s.title}</h3>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{s.sub}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-gold/80 italic">{slide.footnote}</p>
    </div>
  );
}

function SlideProduct({ slide }) {
  return (
    <div className="max-w-6xl mx-auto w-full">
      <h2 className="font-serif text-3xl lg:text-4xl text-white text-center mb-10">{slide.title}</h2>
      <div className="grid md:grid-cols-3 gap-6 mb-10">
        {slide.kpis.map((k, i) => (
          <div key={i} className="luxury-card p-8 text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">{k.label}</p>
            <p className="font-serif text-5xl text-gold mb-2">{k.value}</p>
            <p className="text-xs text-muted-foreground">{k.sub}</p>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
        {slide.bullets.map((b, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
            <span className="text-gold mt-0.5">·</span>
            <span>{b}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideTable({ slide }) {
  return (
    <div className="max-w-6xl mx-auto w-full">
      <h2 className="font-serif text-3xl lg:text-4xl text-white text-center mb-10">{slide.title}</h2>
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gold/30">
            {slide.columns.map((c, i) => (
              <th key={i} className="text-[10px] uppercase tracking-[0.25em] text-gold py-3 px-3">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slide.rows.map((row, i) => (
            <tr key={i} className="border-b border-white/5">
              {row.map((cell, j) => (
                <td key={j} className={`py-4 px-3 text-sm ${j === 0 ? 'text-white' : 'text-muted-foreground'}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-sm text-gold/80 italic text-center mt-8">{slide.footnote}</p>
    </div>
  );
}

function SlidePricing({ slide }) {
  const renderTier = (t, i) => {
    const Icon = t.icon;
    return (
      <div key={i} className={`luxury-card p-6 ${t.highlight ? 'border-gold/40' : ''}`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon size={14} className="text-gold" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t.name}</p>
        </div>
        <p className="font-serif text-2xl text-white mb-1">{t.price}</p>
        {t.promise && <p className="text-[11px] text-gold/80 italic mb-2">{t.promise}</p>}
        <p className="text-xs text-muted-foreground leading-relaxed">{t.body}</p>
      </div>
    );
  };
  return (
    <div className="max-w-6xl mx-auto w-full">
      <h2 className="font-serif text-3xl lg:text-4xl text-white text-center mb-8">{slide.title}</h2>

      {/* Top row - the three HNW tiers (clients) */}
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        {slide.primaryTiers.map(renderTier)}
      </div>

      {/* Bottom row - broker + partner, centred (max-w-2/3) */}
      <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
        {slide.secondaryTiers.map(renderTier)}
      </div>

      <p className="text-sm text-gold/80 italic text-center mt-8">{slide.footnote}</p>
    </div>
  );
}

function SlideWhyNow({ slide }) {
  return (
    <div className="max-w-5xl mx-auto w-full">
      <h2 className="font-serif text-3xl lg:text-4xl text-white text-center mb-12">{slide.title}</h2>
      <div className="space-y-6">
        {slide.points.map((p, i) => {
          const Icon = p.icon;
          return (
            <div key={i} className="luxury-card p-6 flex items-start gap-5">
              <div className="w-12 h-12 border border-gold/30 flex items-center justify-center flex-shrink-0">
                <Icon size={20} className="text-gold" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-white mb-1">{p.label}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SlideMatrix({ slide }) {
  return (
    <div className="max-w-6xl mx-auto w-full">
      <h2 className="font-serif text-2xl lg:text-3xl text-white text-center mb-8">{slide.title}</h2>
      <table className="w-full text-left text-sm mb-8">
        <thead>
          <tr className="border-b border-gold/30">
            {slide.cols.map((c, i) => (
              <th key={i} className={`text-[10px] uppercase tracking-[0.2em] py-3 px-2 ${i === slide.cols.length - 1 ? 'text-gold' : 'text-muted-foreground'}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slide.rows.map((row, i) => (
            <tr key={i} className="border-b border-white/5">
              {row.map((cell, j) => (
                <td key={j} className={`py-3 px-2 ${j === 0 ? 'text-white' : j === row.length - 1 ? 'text-gold font-medium' : 'text-muted-foreground'}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid md:grid-cols-3 gap-4">
        {slide.moat.map((m, i) => (
          <div key={i} className="luxury-card p-4">
            <p className="text-gold text-[10px] uppercase tracking-[0.25em] mb-2">Moat {i + 1}</p>
            <p className="text-white text-sm font-medium mb-1">{m.title}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{m.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideSecurity({ slide }) {
  return (
    <div className="max-w-6xl mx-auto w-full">
      <div className="text-center mb-8">
        <h2 className="font-serif text-3xl lg:text-4xl text-white mb-3">{slide.title}</h2>
        <p className="text-sm text-muted-foreground max-w-3xl mx-auto leading-relaxed">{slide.body}</p>
      </div>

      {/* 4 pillars */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {slide.pillars.map((p, i) => {
          const Icon = p.icon;
          return (
            <div key={i} className="luxury-card p-5 flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center">
                <Icon size={18} className="text-gold" />
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium mb-1">{p.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compliance strip */}
      <div className="border-t border-gold/20 pt-4">
        <p className="text-gold text-[10px] uppercase tracking-[0.3em] mb-3 text-center">Compliance & posture</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {slide.compliance.map((c, i) => (
            <div key={i} className="text-center">
              <p className="text-white text-sm font-medium">{c.label}</p>
              <p className="text-[10px] text-muted-foreground mt-1 leading-tight">{c.sub}</p>
            </div>
          ))}
        </div>
        {slide.footnote && (
          <p className="text-[10px] text-muted-foreground text-center mt-4 italic">{slide.footnote}</p>
        )}
      </div>
    </div>
  );
}


function SlideGtm({ slide }) {
  return (
    <div className="max-w-6xl mx-auto w-full">
      <h2 className="font-serif text-3xl lg:text-4xl text-white text-center mb-12">{slide.title}</h2>
      <div className="space-y-5">
        {slide.phases.map((p, i) => (
          <div key={i} className="luxury-card p-6 flex items-start gap-6 flex-wrap">
            <div className="flex-shrink-0">
              <p className="text-gold font-serif text-4xl">{p.num}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{p.timeline}</p>
            </div>
            <div className="flex-1 min-w-[200px]">
              <h3 className="font-serif text-lg text-white mb-1">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Outcome</p>
              <p className="text-gold text-sm font-medium">{p.metric}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideTraction({ slide }) {
  return (
    <div className="max-w-5xl mx-auto w-full text-center">
      <h2 className="font-serif text-3xl lg:text-4xl text-white mb-3">{slide.title}</h2>
      <p className="text-base text-gold/90 mb-10">{slide.line}</p>
      <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
        {slide.pills.map((p, i) => (
          <span key={i} className="px-4 py-2 border border-gold/30 rounded-full text-xs text-muted-foreground">{p}</span>
        ))}
      </div>
    </div>
  );
}

function SlideVision({ slide }) {
  return (
    <div className="max-w-3xl mx-auto w-full text-center">
      <h2 className="font-serif text-4xl lg:text-6xl text-white leading-tight mb-10">
        {slide.title}
      </h2>
      <div className="h-px w-20 bg-gold mx-auto mb-8" />
      <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
        {slide.body}
      </p>
      <Crown size={28} className="text-gold mx-auto mt-10 opacity-60" />
    </div>
  );
}


export default function InvestorDeck() {
  const [idx, setIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const total = SLIDES.length;
  const slide = SLIDES[idx];

  const next = () => setIdx(i => Math.min(i + 1, total - 1));
  const prev = () => setIdx(i => Math.max(i - 1, 0));
  const print = () => window.print();

  const downloadOnePager = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/one-pager`;
    window.open(url, '_blank');
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try { await document.documentElement.requestFullscreen(); setFullscreen(true); } catch {}
    } else {
      try { await document.exitFullscreen(); setFullscreen(false); } catch {}
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); prev(); }
      else if (e.key === 'Home') setIdx(0);
      else if (e.key === 'End') setIdx(total - 1);
      else if (e.key === 'p' && (e.metaKey || e.ctrlKey)) { /* let browser handle */ }
      else if (e.key === 'f') toggleFullscreen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const Renderer = useMemo(() => ({
    cover:   SlideCover,
    problem: SlideProblem,
    insight: SlideInsight,
    product: SlideProduct,
    table:   SlideTable,
    pricing: SlidePricing,
    'why-now': SlideWhyNow,
    matrix:  SlideMatrix,
    security: SlideSecurity,
    gtm:     SlideGtm,
    traction: SlideTraction,
    vision:  SlideVision,
  }[slide.type] || SlideProblem), [slide.type]);

  return (
    <div className="investor-deck min-h-screen bg-[#050505] text-white">
      <style>{`
        .investor-deck .slide-shell { min-height: calc(100vh - 0px); }
        .investor-deck .luxury-card { background: #0F0F0F; border: 1px solid #262626; border-radius: 0; }
        @media print {
          body { background: #050505 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .investor-deck .deck-controls { display: none !important; }
          .investor-deck .deck-print-stack { display: block !important; }
          .investor-deck .deck-current { display: none !important; }
          .investor-deck .deck-print-stack > section { page-break-after: always; min-height: 100vh; }
          .investor-deck .deck-print-stack > section:last-child { page-break-after: auto; }
          @page { size: 1280px 720px; margin: 0; }
        }
        .investor-deck .deck-print-stack { display: none; }
      `}</style>

      {/* Top control bar - hidden when printing */}
      <div className="deck-controls fixed top-0 left-0 right-0 z-50 flex items-center justify-between gap-3 px-6 py-3 bg-[#050505]/85 backdrop-blur border-b border-white/5">
        <Link to="/" className="text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-gold flex items-center gap-2" data-testid="deck-back">
          <ChevronLeft size={14} /> Exit deck
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={prev} disabled={idx === 0} className="p-2 border border-white/10 rounded hover:border-gold/40 disabled:opacity-30" data-testid="deck-prev"><ChevronLeft size={14} /></button>
          <span className="text-xs text-muted-foreground tabular-nums px-2">{String(idx + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
          <button onClick={next} disabled={idx === total - 1} className="p-2 border border-white/10 rounded hover:border-gold/40 disabled:opacity-30" data-testid="deck-next"><ChevronRight size={14} /></button>
          <button onClick={toggleFullscreen} className="p-2 border border-white/10 rounded hover:border-gold/40 ml-2" data-testid="deck-fullscreen" title="Toggle fullscreen (F)">
            {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button onClick={print} className="ml-2 px-3 py-2 bg-gold text-black text-xs uppercase tracking-wider rounded hover:bg-amber-300 inline-flex items-center gap-1" data-testid="deck-print" title="Print to PDF (Cmd/Ctrl+P)">
            <Printer size={12} /> PDF
          </button>
          <button onClick={downloadOnePager} className="ml-1 px-3 py-2 border border-gold/40 text-gold text-xs uppercase tracking-wider rounded hover:bg-gold/10 inline-flex items-center gap-1" data-testid="deck-one-pager" title="Download one-page leave-behind">
            <Download size={12} /> 1-page
          </button>
        </div>
      </div>

      {/* Current slide */}
      <div className="deck-current pt-14 min-h-screen" data-testid={`deck-slide-${slide.id}`}>
        <SlideShell slide={slide} index={idx} total={total}>
          <Renderer slide={slide} />
        </SlideShell>
      </div>

      {/* Print-only stack - rendered ONLY when printing */}
      <div className="deck-print-stack">
        {SLIDES.map((s, i) => {
          const R = ({
            cover: SlideCover, problem: SlideProblem, insight: SlideInsight,
            product: SlideProduct, table: SlideTable, pricing: SlidePricing,
            'why-now': SlideWhyNow, matrix: SlideMatrix, security: SlideSecurity,
            gtm: SlideGtm, traction: SlideTraction, vision: SlideVision,
          }[s.type] || SlideProblem);
          return (
            <section key={s.id}>
              <SlideShell slide={s} index={i} total={total}>
                <R slide={s} />
              </SlideShell>
            </section>
          );
        })}
      </div>

      {/* Slide-number HUD for keyboard navigation hint */}
      <div className="deck-controls fixed bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] text-muted-foreground/40">
        <kbd className="px-1.5 py-0.5 border border-white/10 rounded">←</kbd>
        <kbd className="px-1.5 py-0.5 border border-white/10 rounded">→</kbd>
        <span className="ml-2">arrow keys to navigate · F for fullscreen · PDF button to print</span>
      </div>
    </div>
  );
}
